import { useState } from 'react';
import { api, ErrorApi, type Yo } from '../services/api';
import { Aviso, Boton, Tarjeta, Titulo } from '../components/ui';
import { CambiarPin } from './CambiarPin';

/**
 * Lo de cada uno: su nombre y su PIN.
 *
 * Cambiar el PIN desde acá es el caso de "le presté el celular a alguien":
 * al cambiarlo se cierran todas las sesiones abiertas de esa persona, salvo
 * la que está usando ahora.
 */
export function MiCuenta({
  yo,
  onCambio,
  onVolver,
  onAyuda,
  onGestion,
  onSalir,
}: {
  yo: Yo;
  onCambio: () => void;
  onVolver: () => void;
  onAyuda: () => void;
  onGestion: () => void;
  onSalir: () => Promise<void>;
}) {
  const [nombre, setNombre] = useState(yo.jugador.nombre);
  const [cambiandoPin, setCambiandoPin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  if (cambiandoPin) {
    return (
      <CambiarPin
        obligatorio={false}
        onListo={() => {
          setCambiandoPin(false);
          setAviso('PIN cambiado. Si tenías la app abierta en otro celular, ahí se cerró.');
          onCambio();
        }}
        onCancelar={() => setCambiandoPin(false)}
      />
    );
  }

  const limpio = nombre.trim();
  const cambio = limpio !== yo.jugador.nombre;

  async function guardarNombre() {
    setOcupado(true);
    setError(null);
    setAviso(null);
    try {
      await api.cambiarNombre(limpio);
      setAviso('Listo. Tus partidos viejos ya aparecen con el nombre nuevo.');
      onCambio();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo cambiar.');
    } finally {
      setOcupado(false);
    }
  }

  async function salir() {
    setOcupado(true);
    setError(null);
    try { await onSalir(); }
    catch (e) { setError(e instanceof Error ? e.message : 'No se pudo cerrar la sesión.'); }
    finally { setOcupado(false); }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <Titulo>Mi cuenta</Titulo>
        <Boton tipo="secundario" onClick={onVolver}>
          Volver
        </Boton>
      </div>

      {error && <Aviso tono="error">{error}</Aviso>}
      {aviso && <Aviso>{aviso}</Aviso>}

      {yo.es_dueno && <Tarjeta><Titulo>Mis grupos de Rankealo</Titulo><p className="mt-2 text-sm text-(--color-apagado)">Altas, pruebas gratuitas y pagos de otros grupos.</p><div className="mt-3"><Boton ancho onClick={onGestion}>Administrar grupos</Boton></div></Tarjeta>}

      <Tarjeta>
        <Titulo chico>Cómo te ven los demás</Titulo>
        <div className="mt-3 flex gap-2">
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && cambio && limpio.length >= 2) void guardarNombre();
            }}
            maxLength={40}
            className="min-h-12 min-w-0 flex-1 rounded-xl border border-(--color-borde)
              bg-(--color-fondo) px-4 text-base outline-none focus:border-(--color-acento)"
          />
          <Boton onClick={guardarNombre} deshabilitado={ocupado || !cambio || limpio.length < 2}>
            Guardar
          </Boton>
        </div>
        <p className="mt-2 text-xs text-(--color-apagado)">
          Si otro ya usa ese nombre, agregale el apellido o un apodo.
        </p>
      </Tarjeta>

      <Tarjeta>
        <Titulo chico>Tu PIN</Titulo>
        <p className="mt-1 text-sm text-(--color-apagado)">
          Si alguien más lo vio, o le prestaste el celular, cambialo: se cierra la sesión en
          cualquier otro teléfono donde hayas entrado.
        </p>
        <div className="mt-3">
          <Boton tipo="secundario" ancho onClick={() => setCambiandoPin(true)}>
            Cambiar mi PIN
          </Boton>
        </div>
      </Tarjeta>

      <Tarjeta>
        <Titulo chico>¿Dudas?</Titulo>
        <p className="mt-1 text-sm text-(--color-apagado)">
          Cómo se cargan y confirman los partidos, cómo se arman las tablas y cómo se consigue
          cada logro.
        </p>
        <div className="mt-3">
          <Boton tipo="secundario" ancho onClick={onAyuda}>
            Preguntas frecuentes
          </Boton>
        </div>
      </Tarjeta>
      <Boton tipo="secundario" ancho deshabilitado={ocupado} onClick={() => void salir()}>Salir de mi cuenta</Boton>
    </div>
  );
}
